import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { MdStar } from "react-icons/md";
import { Link } from "react-router-dom";
import { obtenerJuegos, listPublicRooms } from "../api";
import ParticleBackground from "../components/ParticleBackground";


export default function PricingPage() {
  const [juegos, setJuegos] = useState([]);
  const [publicRooms, setPublicRooms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("");
  const [minPlayers, setMinPlayers] = useState("");
  const [gameQuery, setGameQuery] = useState("");
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [isPRFilterOpen, setIsPRFilterOpen] = useState(false);
  const [prDate, setPrDate] = useState("");
  const [prCategory, setPrCategory] = useState("");
  const [prName, setPrName] = useState("");
  const [prMinSpots, setPrMinSpots] = useState("");
  const [prBookedBy, setPrBookedBy] = useState("");
  const [prVisibleCount, setPrVisibleCount] = useState(3);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        const [juegosRes, publicRoomsRes] = await Promise.all([
          obtenerJuegos(),
          listPublicRooms(),
        ]);
        if (cancelled) return;
        setJuegos(Array.isArray(juegosRes) ? juegosRes : juegosRes?.juegos || []);
        const now = new Date();
        const rooms = (Array.isArray(publicRoomsRes) ? publicRoomsRes : publicRoomsRes?.rooms || [])
          .filter(r => {
            const start = new Date(r.start_time);
            return start >= now && (r.capacidad_maxima ?? 0) - (r.jugadores_actuales ?? 0) > 0;
          })
          .sort((a, b) => new Date(a.start_time) - new Date(b.start_time));
        setPublicRooms(rooms);
      } catch (e) {
        console.error(e);
        setError(e?.message || "Failed to load pricing data");
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, []);

  // Reset visible public rooms when filters or list change
  useEffect(() => {
    setPrVisibleCount(3);
  }, [prDate, prCategory, prName, prMinSpots, prBookedBy, publicRooms]);

  const categories = Array.from(new Set(juegos.map(j => j.categoria_nombre).filter(Boolean)));
  const minPlayersNum = Number(minPlayers) || 0;
  const q = gameQuery.trim().toLowerCase();
  const hasGameFilters = Boolean(q || selectedCategory || minPlayersNum);
  const filteredGames = juegos.filter(j => (
    (q ? (j.nombre || "").toLowerCase().includes(q) : true) &&
    (selectedCategory ? j.categoria_nombre === selectedCategory : true) &&
    (minPlayersNum ? (j.capacidad_maxima ?? 0) >= minPlayersNum : true)
  ));

  const mappedGames = filteredGames.map((j) => ({
    id: j.id,
    title: j.nombre,
    description: j.descripcion,
    categoria: j.categoria_nombre,
    details: [
      `${j.duracion} minutes`,
      `Base capacity: ${j.capacidad_base} players`,
      `Max players: ${j.capacidad_maxima}`,
      `Extra per player: $${Number(j.precio_extra_por_jugador || 0).toLocaleString("en-US", { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`,
    ],
    price: `$${Number(j.precio_base || 0).toLocaleString("en-US", { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`,
    image: j.imagen_url || "/images/default-game.jpg",
    isPopular: false,
  }));

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-[#141318] to-[#1F2937] text-white font-inter relative overflow-hidden flex items-center justify-center">
        <div className="text-center">
          <div className="relative mb-8">
            <div className="w-16 h-16 mx-auto border-4 border-[#FFD404]/30 border-t-[#FFD404] rounded-full animate-spin"></div>
            <div className="absolute inset-0 w-12 h-12 mx-auto mt-2 border-4 border-[#566D87]/30 border-t-[#566D87] rounded-full animate-spin" style={{animationDirection: 'reverse', animationDuration: '1.5s'}}></div>
          </div>
          <h2 className="audiowide-font text-2xl mb-2 text-[#FFD404] drop-shadow-[0_0_15px_rgba(255,212,4,0.35)]">
            Loading Arena
          </h2>
          <p className="text-white/70 text-sm animate-pulse">
            Preparing your laser tag experience...
          </p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-near-black text-light-gray font-inter relative text-center p-8">
        Error: {error}
      </div>
    );
  }

  const gameNameById = Object.fromEntries(juegos.map(g => [g.id, g.nombre]));
  const categoryByGameId = Object.fromEntries(juegos.map(g => [g.id, g.categoria_nombre]));
  const prMinSpotsNum = Number(prMinSpots) || 0;
  const hasPRFilters = Boolean(prDate || prCategory || prName || prMinSpotsNum || prBookedBy);
  const filteredPublicRooms = publicRooms.filter(r => {
    const gameName = (gameNameById[r.game_id] || "").toLowerCase();
    const roomDateISO = new Date(r.start_time).toISOString().slice(0, 10);
    const available = (r.capacidad_maxima ?? 0) - (r.jugadores_actuales ?? 0);
    const bookedBy = (r.creador_nombre || r.creador_email || "").toLowerCase();
    return (
      (prDate ? roomDateISO === prDate : true) &&
      (prCategory ? categoryByGameId[r.game_id] === prCategory : true) &&
      (prName ? gameName.includes(prName.trim().toLowerCase()) : true) &&
      (prMinSpotsNum ? available >= prMinSpotsNum : true) &&
      (prBookedBy ? bookedBy.includes(prBookedBy.trim().toLowerCase()) : true)
    );
  });


  return (
    <div className="min-h-screen bg-gradient-to-b from-[#141318] to-[#1F2937] text-white font-inter relative overflow-hidden">
      <div className="absolute inset-0 z-0">
        <ParticleBackground />
      </div>
      {/* Hero (FIRST) */}
      <section className="py-16 px-4 bg-transparent relative z-20" as="header">
        <div className="max-w-6xl mx-auto text-center">
          <h1 className="audiowide-font text-[clamp(2rem,5vw,3.5rem)] mb-3 drop-shadow-[0_0_15px_rgba(255,255,255,0.35)]">
            Laser Tag Pricing & Booking
          </h1>
          <p className="text-[clamp(1rem,2.5vw,1.25rem)] text-white/90 max-w-2xl mx-auto">
            Discover top laser tag experiences. Create private battles or join public rooms in seconds.
          </p>
        </div>
      </section>

      {/* How it works */}
      <section className="py-6 px-4 bg-transparent relative z-10" as="section">
        <div className="max-w-7xl mx-auto grid md:grid-cols-2 gap-6">
          <div className="rounded-2xl border border-[#FFFFFF14] bg-[#ffffff0d] backdrop-blur-sm p-5">
            <h3 className="audiowide-font text-lg text-[#FFD404] mb-2">Create a new room</h3>
            <p className="text-white/85 mb-2">Choose a game and time, then decide if your room is private or public.</p>
            <ul className="list-disc list-inside text-white/80 text-sm space-y-1">
              <li><span className="font-semibold">Private</span>: only you and your friends play — no outsiders.</li>
              <li><span className="font-semibold">Public</span>: your room is visible so others can join if there’s space. Great when no room exists at your time and you’d like more players.</li>
            </ul>
          </div>
          <div className="rounded-2xl border border-[#FFFFFF14] bg-[#ffffff0d] backdrop-blur-sm p-5">
            <h3 className="audiowide-font text-lg text-[#566D87] mb-2">Join a public room</h3>
            <p className="text-white/85 mb-2">Find an existing public room and jump in if there are available spots.</p>
            <ul className="list-disc list-inside text-white/80 text-sm space-y-1">
              <li>Good for playing solo or with friends when you don’t mind sharing the arena.</li>
              <li>Meet new players and fill empty slots fast.</li>
            </ul>
          </div>
        </div>
      </section>

      {/* Filters for games only */}

      {/* Section divider: before Create a new room */}
      <section aria-hidden="true" className="relative z-15 py-8">
        <div className="max-w-7xl mx-auto px-4">
          <div className="relative">
            <div className="absolute inset-0 pointer-events-none" style={{ background: 'radial-gradient(600px 80px at 50% 50%, rgba(255, 212, 4, 0.08), transparent 70%)' }} />
            <div className="w-full h-px bg-gradient-to-r from-transparent via-[#FFD404] to-transparent opacity-70"></div>
          </div>
        </div>
      </section>

      {/* Games grid */}
      <section id="create-room" className="py-10 px-4 bg-transparent relative z-20">
        <div className="max-w-7xl mx-auto mb-10">
          <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between mb-6">
            <div>
              <h2 className="audiowide-font text-[clamp(1.8rem,3.5vw,2.25rem)] text-[#566D87]">
                Create a new room (public or private)
              </h2>
              <p className="text-white/70 text-sm max-w-2xl mt-1">Private = only your group. Public = visible so others can join your time slot if there’s space.</p>
            </div>
            <button
              onClick={() => setIsFilterOpen(true)}
              className="bg-[#FFD404] hover:bg-[#ffde3a] text-[#012727] px-5 py-3 rounded-full text-sm font-bold transition-all transform hover:scale-105 shadow-[0_0_25px_rgba(255,212,4,0.35)]"
            >
              Filter
            </button>
          </div>

          {isFilterOpen && typeof document !== "undefined" && createPortal(
            <div className="fixed inset-0 z-[2147483647] flex items-center justify-center p-2">
              <div className="absolute inset-0 bg-black/70" onClick={() => setIsFilterOpen(false)} />
              <div role="dialog" aria-modal="true" className="relative z-[2147483647] w-full max-w-[95vw] sm:max-w-3xl lg:max-w-5xl mx-auto rounded-2xl border border-[#FFFFFF14] bg-[#141318] backdrop-blur-md p-4 sm:p-6 max-h-[90vh] overflow-auto overflow-x-hidden">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="audiowide-font text-[#566D87] text-xl">Filters</h3>
                  <button
                    onClick={() => setIsFilterOpen(false)}
                    className="text-white/80 hover:text-white text-2xl leading-none"
                    aria-label="Close filters"
                  >
                    ×
                  </button>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="min-w-0 rounded-xl border border-[#FFFFFF14] bg-[#ffffff0d] backdrop-blur-sm p-4 md:col-span-2">
                    <label className="block text-base mb-2 text-white/85">Game</label>
                    <input
                      type="text"
                      placeholder="e.g. Photon Siege"
                      value={gameQuery}
                      onChange={(e) => setGameQuery(e.target.value)}
                      className="w-full h-12 text-base sm:text-lg bg-[#00000066] border border-[#FFFFFF1f] rounded-xl px-4 text-white placeholder:text-white/70 focus:outline-none focus:ring-2 focus:ring-[#FFD404]/40"
                    />
                  </div>
                  <div className="min-w-0 rounded-xl border border-[#FFFFFF14] bg-[#ffffff0d] backdrop-blur-sm p-4">
                    <label className="block text-base mb-2 text-white/85">Category</label>
                    <select
                      value={selectedCategory}
                      onChange={(e) => setSelectedCategory(e.target.value)}
                      className="w-full h-12 text-base sm:text-lg bg-[#00000066] border border-[#FFFFFF1f] rounded-xl px-4 text-white focus:outline-none focus:ring-2 focus:ring-[#FFD404]/40"
                    >
                      <option value="">All categories</option>
                      {categories.map(cat => (
                        <option key={cat} value={cat}>{cat}</option>
                      ))}
                    </select>
                  </div>
                  <div className="min-w-0 rounded-xl border border-[#FFFFFF14] bg-[#ffffff0d] backdrop-blur-sm p-4">
                    <label className="block text-base mb-2 text-white/85">Min players</label>
                    <input
                      type="number"
                      min={0}
                      placeholder="e.g. 4"
                      value={minPlayers}
                      onChange={(e) => setMinPlayers(e.target.value)}
                      className="w-full h-12 text-base sm:text-lg bg-[#00000066] border border-[#FFFFFF1f] rounded-xl px-4 text-white placeholder:text-white/70 focus:outline-none focus:ring-2 focus:ring-[#FFD404]/40"
                    />
                  </div>
                </div>
                <div className="flex justify-end gap-3 mt-6">
                  <button
                    onClick={() => { setSelectedCategory(""); setMinPlayers(""); setGameQuery(""); }}
                    className="px-5 py-3 rounded-full text-sm font-bold border border-[#FFFFFF1f] text-white/90 hover:bg-white/10 transition"
                  >
                    Clear
                  </button>
                  <button
                    onClick={() => setIsFilterOpen(false)}
                    className="bg-[#FFD404] hover:bg-[#ffde3a] text-[#012727] px-5 py-3 rounded-full text-sm font-bold transition-all transform hover:scale-105 shadow-[0_0_25px_rgba(255,212,4,0.35)]"
                  >
                    Apply
                  </button>
                </div>
              </div>
            </div>,
            document.body
          )}

          {mappedGames.length > 0 ? (
            <div className="grid sm:grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
              {mappedGames.map((game, index) => (
                <div
                  key={game.id}
                  className="relative rounded-2xl p-6 flex flex-col shadow-xl border border-[#FFFFFF14] bg-[#ffffff08] backdrop-blur-sm hover:scale-[1.03] hover:border-[#FFD404]/40 hover:shadow-[0_0_25px_rgba(255,212,4,0.15)] transition-transform duration-200 group"
                  style={{ animation: `cardFadeIn 0.6s ${index * 0.1}s both` }}
                >
                  {game.isPopular && (
                    <span className="absolute top-4 right-4 bg-[#FFD404] text-[#012727] text-xs font-bold px-3 py-1 rounded-full shadow-[0_0_15px_rgba(255,212,4,0.4)] z-10">
                      <MdStar className="inline-block mr-1" /> Popular
                    </span>
                  )}
                  <div className="flex-1">
                    <h3 className="text-2xl font-bold mb-2 group-hover:text-[#FFD404] transition-colors">{game.title}</h3>
                    <span className="text-xs inline-block bg-[#ffffff12] text-white/90 px-3 py-1 rounded-full mb-3 uppercase tracking-wide border border-[#FFFFFF14]">
                      {game.categoria}
                    </span>
                    <p className="text-white/85 mb-3 text-sm">{game.description}</p>
                    <ul className="text-white/80 mb-4 text-sm">
                      {game.details.map((detail, i) => (
                        <li key={i} className="flex items-center gap-2">
                          <span className="inline-block w-2 h-2 bg-[#FFD404] rounded-full"></span>
                          {detail}
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div className="flex items-center justify-between mt-2">
                    <p className="text-lg font-bold">{game.price}</p>
                    <Link
                      to={`/checkout/${game.id}`}
                      className="bg-[#FFD404] no-underline hover:bg-[#ffde3a] text-[#012727] px-5 py-2 rounded-full text-sm font-bold transition-all transform hover:scale-105 shadow-[0_0_25px_rgba(255,212,4,0.35)]"
                    >
                      BOOK NOW
                    </Link>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="w-full flex items-center justify-center py-16">
              <div className="max-w-xl w-full text-center rounded-2xl border border-[#FFFFFF14] bg-[#ffffff0d] backdrop-blur-sm p-8">
                <div className="text-4xl mb-3">🔍</div>
                <h3 className="text-2xl font-semibold mb-2">{hasGameFilters ? 'No results found' : 'No games available'}</h3>
                <p className="text-white/80 mb-6">{hasGameFilters ? 'Try adjusting your search or filters to find more games.' : 'We’re updating our catalog. Please check back soon.'}</p>
                {hasGameFilters && (
                  <div className="flex items-center justify-center">
                    <button
                      onClick={() => { setSelectedCategory(""); setMinPlayers(""); setGameQuery(""); }}
                      className="px-6 py-3 rounded-full text-sm font-bold border border-[#FFFFFF1f] text-white/90 hover:bg-white/10 transition"
                    >
                      Clear filters
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Public rooms */}
      {/* Section divider */}
      <section aria-hidden="true" className="relative z-15 py-8">
        <div className="max-w-7xl mx-auto px-4">
          <div className="relative">
            <div className="absolute inset-0 pointer-events-none" style={{ background: 'radial-gradient(600px 80px at 50% 50%, rgba(255, 212, 4, 0.08), transparent 70%)' }} />
            <div className="w-full h-px bg-gradient-to-r from-transparent via-[#FFD404] to-transparent opacity-70"></div>
          </div>
        </div>
      </section>

      {/* Public rooms */}
      <section className="py-10 px-4 bg-transparent relative z-20">
        <div className="max-w-7xl mx-auto mb-4">
          <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between mb-6">
            <div>
              <h2 className="audiowide-font text-[clamp(1.8rem,3.5vw,2.25rem)] text-[#566D87]">Join a public room</h2>
              <p className="text-white/70 text-sm max-w-2xl mt-1">Join an existing public room. Ideal if you’re playing solo or with friends and don’t mind sharing the arena.</p>
            </div>
            <button
              onClick={() => setIsPRFilterOpen(true)}
              className="bg-[#FFD404] hover:bg-[#ffde3a] text-[#012727] px-5 py-3 rounded-full text-sm font-bold transition-all transform hover:scale-105 shadow-[0_0_25px_rgba(255,212,4,0.35)]"
            >
              Filter
            </button>
          </div>

          {isPRFilterOpen && typeof document !== "undefined" && createPortal(
            <div className="fixed inset-0 z-[2147483647] flex items-center justify-center p-2">
              <div className="absolute inset-0 bg-black/70" onClick={() => setIsPRFilterOpen(false)} />
              <div role="dialog" aria-modal="true" className="relative z-[2147483647] w-full max-w-[95vw] sm:max-w-3xl lg:max-w-5xl mx-auto rounded-2xl border border-[#FFFFFF14] bg-[#141318] backdrop-blur-md p-4 sm:p-6 max-h-[90vh] overflow-auto overflow-x-hidden">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="audiowide-font text-[#566D87] text-xl">Public Room Filters</h3>
                  <button
                    onClick={() => setIsPRFilterOpen(false)}
                    className="text-white/80 hover:text-white text-2xl leading-none"
                    aria-label="Close filters"
                  >
                    ×
                  </button>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="min-w-0 rounded-xl border border-[#FFFFFF14] bg-[#ffffff0d] backdrop-blur-sm p-4 md:col-span-2">
                    <label className="block text-base mb-2 text-white/85">Game name</label>
                    <input
                      type="text"
                      placeholder="e.g. Photon Siege"
                      value={prName}
                      onChange={(e) => setPrName(e.target.value)}
                      className="w-full h-12 text-base sm:text-lg bg-[#00000066] border border-[#FFFFFF1f] rounded-xl px-4 text-white placeholder:text-white/70 focus:outline-none focus:ring-2 focus:ring-[#FFD404]/40"
                    />
                  </div>
                  <div className="min-w-0 rounded-xl border border-[#FFFFFF14] bg-[#ffffff0d] backdrop-blur-sm p-4">
                    <label className="block text-base mb-2 text-white/85">Date</label>
                    <input
                      type="date"
                      value={prDate}
                      onChange={(e) => setPrDate(e.target.value)}
                      className="w-full h-12 text-base sm:text-lg bg-[#00000066] border border-[#FFFFFF1f] rounded-xl px-4 text-white focus:outline-none focus:ring-2 focus:ring-[#FFD404]/40"
                    />
                  </div>
                  <div className="min-w-0 rounded-xl border border-[#FFFFFF14] bg-[#ffffff0d] backdrop-blur-sm p-4">
                    <label className="block text-base mb-2 text-white/85">Category</label>
                    <select
                      value={prCategory}
                      onChange={(e) => setPrCategory(e.target.value)}
                      className="w-full h-12 text-base sm:text-lg bg-[#00000066] border border-[#FFFFFF1f] rounded-xl px-4 text-white focus:outline-none focus:ring-2 focus:ring-[#FFD404]/40"
                    >
                      <option value="">All categories</option>
                      {categories.map(cat => (
                        <option key={cat} value={cat}>{cat}</option>
                      ))}
                    </select>
                  </div>
                  <div className="min-w-0 rounded-xl border border-[#FFFFFF14] bg-[#ffffff0d] backdrop-blur-sm p-4">
                    <label className="block text-base mb-2 text-white/85">Min available spots</label>
                    <input
                      type="number"
                      min={0}
                      placeholder="e.g. 2"
                      value={prMinSpots}
                      onChange={(e) => setPrMinSpots(e.target.value)}
                      className="w-full h-12 text-base sm:text-lg bg-[#00000066] border border-[#FFFFFF1f] rounded-xl px-4 text-white placeholder:text-white/70 focus:outline-none focus:ring-2 focus:ring-[#FFD404]/40"
                    />
                  </div>
                  <div className="min-w-0 rounded-xl border border-[#FFFFFF14] bg-[#ffffff0d] backdrop-blur-sm p-4">
                    <label className="block text-base mb-2 text-white/85">Booked by (name or email)</label>
                    <input
                      type="text"
                      placeholder="e.g. John Doe or john@example.com"
                      value={prBookedBy}
                      onChange={(e) => setPrBookedBy(e.target.value)}
                      className="w-full h-12 text-base sm:text-lg bg-[#00000066] border border-[#FFFFFF1f] rounded-xl px-4 text-white placeholder:text-white/70 focus:outline-none focus:ring-2 focus:ring-[#FFD404]/40"
                    />
                  </div>
                </div>
                <div className="flex justify-end gap-3 mt-6">
                  <button
                    onClick={() => { setPrDate(""); setPrCategory(""); setPrName(""); setPrMinSpots(""); setPrBookedBy(""); }}
                    className="px-5 py-3 rounded-full text-sm font-bold border border-[#FFFFFF1f] text-white/90 hover:bg-white/10 transition"
                  >
                    Clear
                  </button>
                  <button
                    onClick={() => setIsPRFilterOpen(false)}
                    className="bg-[#FFD404] hover:bg-[#ffde3a] text-[#012727] px-5 py-3 rounded-full text-sm font-bold transition-all transform hover:scale-105 shadow-[0_0_25px_rgba(255,212,4,0.35)]"
                  >
                    Apply
                  </button>
                </div>
              </div>
            </div>,
            document.body
          )}

          {filteredPublicRooms.length > 0 ? (
            <>
              <div className="grid sm:grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {filteredPublicRooms.slice(0, prVisibleCount).map((sala, idx) => {
                const available = (sala.capacidad_maxima ?? 0) - (sala.jugadores_actuales ?? 0);
                return (
                  <div
                    key={sala.id}
                    className="relative rounded-2xl p-6 flex flex-col shadow-xl border border-[#FFFFFF14] bg-[#ffffff08] backdrop-blur-sm hover:scale-[1.03] hover:border-[#FFD404]/40 hover:shadow-[0_0_25px_rgba(255,212,4,0.15)] transition-transform duration-200 group"
                    style={{ animation: `cardFadeIn 0.6s ${idx * 0.1}s both` }}
                  >
                    <div className="flex-1">
                      <h3 className="text-2xl font-bold mb-2 group-hover:text-[#FFD404] transition-colors">Public Room #{sala.id}</h3>
                      <span className="text-xs inline-block bg-[#ffffff12] text-white/90 px-3 py-1 rounded-full mb-3 uppercase tracking-wide border border-[#FFFFFF14]">
                        Game: {gameNameById[sala.game_id] || sala.game_id}
                      </span>
                      {(sala.creador_nombre || sala.creador_email) && (
                        <p className="mb-2 text-sm">Booked by: <span className="font-semibold">{sala.creador_nombre || sala.creador_email}</span></p>
                      )}
                      <p className="mb-2 text-sm">
                        Date: <span className="font-semibold">{new Date(sala.start_time).toLocaleDateString()}</span><br />
                        Time: <span className="font-semibold">{new Date(sala.start_time).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })} - {new Date(sala.end_time).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                      </p>
                      <p className="text-[#8CF29B] mb-2 text-sm">Available spots: <span className="font-bold">{available}</span></p>
                      {sala.precio_por_jugador && (
                        <p className="text-[#8CF29B] mb-2 font-bold text-lg">${Number(sala.precio_por_jugador).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 })} <span className="text-xs font-normal text-white/70">per player</span></p>
                      )}
                    </div>
                    <Link
                      to={`/join-public-room?publicRoomId=${sala.id}`}
                      className="mt-4 inline-flex items-center justify-center w-full bg-[#FFD404] no-underline hover:bg-[#ffde3a] text-[#012727] px-5 py-3 rounded-full text-sm font-bold transition-all transform hover:scale-105 shadow-[0_0_25px_rgba(255,212,4,0.35)]"
                    >
                      <span className="inline-block align-middle mr-2">🌐</span>Join this room
                    </Link>
                  </div>
                );
              })}
              </div>
              {filteredPublicRooms.length > prVisibleCount && (
                <div className="flex justify-center mt-6">
                  <button
                    onClick={() => setPrVisibleCount(c => c + 3)}
                    className="bg-[#FFD404] hover:bg-[#ffde3a] text-[#012727] px-6 py-3 rounded-full text-sm font-bold transition-all transform hover:scale-105 shadow-[0_0_25px_rgba(255,212,4,0.35)]"
                  >
                    Show more
                  </button>
                </div>
              )}
            </>
          ) : (
            <div className="w-full flex items-center justify-center py-16">
              <div className="max-w-xl w-full text-center rounded-2xl border border-[#FFFFFF14] bg-[#ffffff0d] backdrop-blur-sm p-8">
                <div className="text-4xl mb-3">🌐</div>
                <h3 className="text-2xl font-semibold mb-2">{hasPRFilters ? 'No public rooms match your filters' : 'No public rooms available right now'}</h3>
                <p className="text-white/80 mb-6">{hasPRFilters ? 'Try changing the date, category, game name, booker, or available spots to see more options.' : 'Create a public room so others can join your time, or check back soon.'}</p>
                {hasPRFilters ? (
                  <div className="flex items-center justify-center">
                    <button
                      onClick={() => { setPrDate(""); setPrCategory(""); setPrName(""); setPrMinSpots(""); setPrBookedBy(""); }}
                      className="px-6 py-3 rounded-full text-sm font-bold border border-[#FFFFFF1f] text-white/90 hover:bg-white/10 transition"
                    >
                      Clear filters
                    </button>
                  </div>
                ) : (
                  <div className="flex items-center justify-center">
                    <a href="#create-room" className="px-6 py-3 rounded-full text-sm font-bold bg-[#FFD404] text-[#012727] hover:bg-[#ffde3a] transition no-underline">Create a public room</a>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Section divider: after Public rooms */}
      <section aria-hidden="true" className="relative z-15 py-8">
        <div className="max-w-7xl mx-auto px-4">
          <div className="relative">
            <div className="absolute inset-0 pointer-events-none" style={{ background: 'radial-gradient(600px 80px at 50% 50%, rgba(255, 212, 4, 0.08), transparent 70%)' }} />
            <div className="w-full h-px bg-gradient-to-r from-transparent via-[#FFD404] to-transparent opacity-70"></div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-12 px-4 bg-transparent relative z-10" as="section">
        <div className="max-w-5xl mx-auto grid md:grid-cols-3 gap-8 text-center">
          <div>
            <h2 className="audiowide-font text-xl text-[#FFD404] mb-2">Flexible Booking</h2>
            <p className="text-white/85">Choose from public or private rooms, select your favorite game, and book instantly online.</p>
          </div>
          <div>
            <h2 className="audiowide-font text-xl text-[#566D87] mb-2">Transparent Pricing</h2>
            <p className="text-white/85">No hidden fees. See all prices up front, including extra player costs.</p>
          </div>
          <div>
            <h2 className="audiowide-font text-xl text-[#8CF29B] mb-2">Top-Rated Experience</h2>
            <p className="text-white/85">Our arena features the latest technology and a safe, welcoming environment.</p>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-12 px-4 bg-transparent relative z-10" as="section">
        <div className="max-w-4xl mx-auto">
          <h2 className="audiowide-font text-center text-[clamp(1.5rem,3vw,2rem)] mb-8 text-[#566D87]">Frequently Asked Questions</h2>
          <dl className="space-y-6 text-white/90">
            <div>
              <dt className="font-semibold text-[#8CF29B]">How much does it cost?</dt>
              <dd className="ml-4">Base prices and extra per player are shown above for each game.</dd>
            </div>
            <div>
              <dt className="font-semibold text-[#8CF29B]">Can I book a private room?</dt>
              <dd className="ml-4">Yes. Pick a game and proceed to checkout to create a private room.</dd>
            </div>
            <div>
              <dt className="font-semibold text-[#8CF29B]">Minimum age?</dt>
              <dd className="ml-4">Players must be at least 7 years old.</dd>
            </div>
            <div>
              <dt className="font-semibold text-[#8CF29B]">How to join a public room?</dt>
              <dd className="ml-4">Choose any available public room above and click Join.</dd>
            </div>
          </dl>
        </div>
      </section>

      
    </div>
  );
}
